import React,{useEffect} from 'react';
import {motion} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiArrowRight,FiCheck,FiShield,FiLock,FiStar,FiUserCheck,
  FiPackage,FiCheckCircle,FiAward,FiHeart,FiActivity,FiClock
} = FiIcons;

const trustItems = [
  {icon:FiAward,title:'LegitScript Certified',text:'Verified safe and legal healthcare provider'},
  {icon:FiShield,title:'HIPAA Compliant',text:'Bank-level encryption protects your data'},
  {icon:FiCheckCircle,title:'FDA Registered',text:'Medications from licensed 503B pharmacies'},
  {icon:FiUserCheck,title:'Board-Certified',text:'Every case reviewed by a licensed clinician'},
];

const carePaths = [
  {icon:FiHeart,title:'Erectile Dysfunction',text:'Clinician-reviewed prescription options for ED — private, fast, and delivered discreetly to your door.'},
  {icon:FiActivity,title:'Performance & Vitality',text:'Explore care options designed to help you feel your best and perform with confidence.'},
  {icon:FiClock,title:'Ongoing Support',text:'Stay connected with your care team as your needs evolve, with refills handled seamlessly.'},
];

const steps = [
  {num:'01',title:'Private Online Intake',text:'Answer a short, secure questionnaire about your health history and goals — all from home in minutes.'},
  {num:'02',title:'Clinician Review',text:'A licensed medical provider independently reviews your information and determines what\'s right for you.'},
  {num:'03',title:'Discreet Delivery',text:'When prescribed, your medication ships from a licensed US pharmacy in plain, unmarked packaging.'},
];

const testimonials = [
  {name:'David R.',location:'Houston, TX',rating:5,text:'"The process was completely private. I appreciated being able to discuss my concerns without a waiting room."',detail:"Men's Health · ED Care"},
  {name:'Marcus T.',location:'Seattle, WA',rating:5,text:'"My provider took time to review my information carefully. It felt personal — not like a generic script."',detail:'Provider-guided treatment'},
  {name:'James W.',location:'Chicago, IL',rating:5,text:'"Having everything handled discreetly online made it much easier to take the first step. Highly recommend."',detail:'Convenient home delivery'},
];

function HeroSection({navigate}) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#eaf4fb] via-[#f4f9fd] to-[#eef6ff] pb-24 pt-32 lg:pt-44 lg:pb-32">
      <div className="absolute -left-40 -top-20 h-[600px] w-[600px] rounded-full bg-sky-200/40 blur-3xl" />
      <div className="absolute -right-40 top-40 h-[500px] w-[500px] rounded-full bg-blue-200/40 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-[300px] w-[300px] rounded-full bg-teal-100/60 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-16 lg:grid-cols-[1.1fr_0.9fr]">
          <motion.div initial={{opacity:0,y:28}} animate={{opacity:1,y:0}} transition={{duration:0.7}}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-sky-800 shadow-sm">
              <SafeIcon icon={FiShield} className="text-sky-600" />
              Doctor-Prescribed Men's Health Care
            </div>
            <h1 className="text-5xl font-extrabold leading-[1.03] tracking-tight text-slate-950 sm:text-6xl lg:text-7xl">
              Men's health,
              <span className="block mt-1 bg-gradient-to-r from-sky-700 to-blue-500 bg-clip-text text-transparent">
                made simple.
              </span>
            </h1>
            <p className="mt-7 max-w-xl text-xl leading-relaxed text-slate-600">
              Get the private care you deserve — reviewed by a licensed clinician, fulfilled by a US pharmacy, and delivered discreetly to your door.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <button onClick={()=>navigate('/menquiz')}
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-slate-950 px-8 py-4 text-lg font-extrabold text-white shadow-xl shadow-slate-900/20 transition hover:bg-sky-700 hover:shadow-sky-700/25">
                Start Private Evaluation
                <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
              </button>
              <button onClick={()=>navigate('/menquiz')}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-8 py-4 text-lg font-bold text-slate-800 shadow-sm transition hover:border-sky-300 hover:bg-sky-50">
                How It Works
              </button>
            </div>
            <div className="mt-10 grid max-w-lg grid-cols-2 gap-3">
              {['Licensed clinician review','Private online intake','Licensed pharmacy fulfillment','Discreet home delivery'].map((item)=>(
                <div key={item} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                    <SafeIcon icon={FiCheck} className="h-3 w-3" />
                  </span>
                  {item}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} transition={{duration:0.7,delay:0.15}} className="relative hidden lg:block">
            <div className="absolute -inset-4 rounded-[3rem] bg-gradient-to-br from-sky-200/50 to-blue-200/50 blur-2xl" />
            <div className="relative overflow-hidden rounded-[2.5rem] shadow-2xl shadow-slate-900/20">
              <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=1200&q=90"
                alt="Confident man" className="h-[540px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-md text-white">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-400/20 text-sky-200">
                    <SafeIcon icon={FiUserCheck} className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-widest text-sky-200">Care with confidence</p>
                    <p className="mt-0.5 font-bold">Every case reviewed by a licensed physician.</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-200">
                  <SafeIcon icon={FiLock} className="text-sky-300" />
                  Your intake is secure, private, and HIPAA-compliant.
                </div>
              </div>
            </div>
            <div className="absolute -right-6 top-12 rounded-2xl border border-white/80 bg-white p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-500">
                  <SafeIcon icon={FiStar} className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500">Patient Rating</p>
                  <p className="text-xl font-extrabold text-slate-900">4.9 / 5.0</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <section className="border-y border-slate-900/5 bg-slate-950 py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="mb-10 text-center text-xs font-extrabold uppercase tracking-[0.25em] text-sky-400">
          Verified Standards of Care
        </p>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {trustItems.map((item,i)=>(
            <motion.div key={item.title}
              initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}}
              viewport={{once:true}} transition={{delay:i*0.08}}
              className="flex flex-col items-center gap-3 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-400/10 text-sky-400">
                <SafeIcon icon={item.icon} className="h-6 w-6" />
              </div>
              <div>
                <p className="font-extrabold text-white">{item.title}</p>
                <p className="mt-1 text-sm text-slate-400">{item.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CareSection({navigate}) {
  return (
    <section className="bg-white py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-extrabold uppercase tracking-[0.2em] text-sky-700">Care on your terms</p>
          <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Confident care without the waiting room.
          </h2>
          <p className="mt-5 text-xl leading-relaxed text-slate-600">
            Whether you want to address ED or explore men's wellness options, a licensed provider can help you understand what's available.
          </p>
        </div>
        <div className="mt-14 grid gap-7 md:grid-cols-3">
          {carePaths.map((path,i)=>(
            <motion.div key={path.title}
              initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}}
              viewport={{once:true}} transition={{delay:i*0.1}}
              onClick={()=>navigate('/menquiz')}
              className="group cursor-pointer rounded-[2rem] border border-slate-100 bg-white p-8 shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50 text-sky-700 transition group-hover:bg-sky-100">
                <SafeIcon icon={path.icon} className="h-6 w-6" />
              </div>
              <h3 className="mt-7 text-2xl font-extrabold tracking-tight text-slate-900">{path.title}</h3>
              <p className="mt-4 leading-relaxed text-slate-600">{path.text}</p>
              <div className="mt-8 inline-flex items-center gap-2 font-extrabold text-sky-700">
                Start evaluation
                <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  return (
    <section className="relative overflow-hidden bg-slate-950 py-28 text-white">
      <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-sky-900/30 blur-3xl" />
      <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-blue-900/20 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-extrabold uppercase tracking-[0.2em] text-sky-400">Simple from start to finish</p>
          <h2 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">A discreet path to care.</h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-300">
            From intake to delivery, RenewRX makes men's health care private, fast, and stress-free.
          </p>
        </div>
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {steps.map((step,i)=>(
            <motion.div key={step.num}
              initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}}
              viewport={{once:true}} transition={{delay:i*0.1}}
              className="rounded-[2rem] border border-slate-700 bg-slate-900 p-8">
              <span className="text-6xl font-extrabold text-sky-400/25">{step.num}</span>
              <h3 className="mt-5 text-2xl font-extrabold">{step.title}</h3>
              <p className="mt-4 leading-relaxed text-slate-400">{step.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AuthoritySection({navigate}) {
  return (
    <section className="relative overflow-hidden bg-[#eaf4fb] py-28">
      <div className="absolute -left-40 top-10 h-96 w-96 rounded-full bg-sky-200/50 blur-3xl" />
      <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-blue-200/50 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-sm font-extrabold uppercase tracking-[0.2em] text-sky-700">Expert care built around privacy</p>
            <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Clinical guidance you can feel confident about.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">
              RenewRX connects you with licensed medical professionals who can help you explore appropriate prescription treatment options from the comfort of home.
            </p>
            <div className="mt-8 border-l-4 border-sky-500 pl-5">
              <p className="text-sm font-semibold leading-relaxed text-slate-600">
                Prescription eligibility, medication selection, and dosage are determined solely through an independent clinical review.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-4">
              {[
                {icon:FiUserCheck,label:'Licensed clinician review'},
                {icon:FiLock,label:'Private by design'},
                {icon:FiPackage,label:'Pharmacy fulfillment'},
                {icon:FiCheckCircle,label:'Ongoing care support'},
              ].map((item)=>(
                <div key={item.label} className="flex items-center gap-3 rounded-2xl border border-white bg-white p-4 shadow-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700">
                    <SafeIcon icon={item.icon} className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-bold text-slate-800">{item.label}</span>
                </div>
              ))}
            </div>
            <button onClick={()=>navigate('/menquiz')}
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-slate-950 px-8 py-4 text-lg font-extrabold text-white shadow-xl transition hover:bg-sky-700">
              Start Your Private Evaluation
              <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-[3rem] bg-sky-200/40 blur-2xl" />
            <div className="relative overflow-hidden rounded-[2.5rem] shadow-2xl shadow-sky-950/10">
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=900&q=85"
                alt="Man comfortably using private healthcare from home" className="h-[500px] w-full object-cover" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/30 bg-slate-950/75 px-5 py-4 text-white backdrop-blur-md">
                <p className="text-xs font-extrabold uppercase tracking-wider text-sky-200">Your care</p>
                <p className="mt-1 font-bold">Reviewed thoughtfully, every step of the way.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="bg-white py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-extrabold uppercase tracking-[0.2em] text-sky-700">Patient Experiences</p>
          <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">Care that fits real life.</h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {testimonials.map((t,i)=>(
            <motion.div key={t.name}
              initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}}
              viewport={{once:true}} transition={{delay:i*0.1}}
              className="flex flex-col rounded-[2rem] border border-slate-100 bg-white p-8 shadow-lg shadow-slate-900/5">
              <div className="flex gap-1 text-amber-400">
                {[...Array(t.rating)].map((_,j)=>(
                  <SafeIcon key={j} icon={FiStar} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <blockquote className="mt-5 flex-grow text-lg leading-relaxed text-slate-700">{t.text}</blockquote>
              <div className="mt-8 border-t border-slate-100 pt-5">
                <p className="font-extrabold text-slate-900">{t.name}</p>
                <p className="mt-1 text-sm text-slate-500">{t.location} · {t.detail}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <p className="mt-8 text-center text-xs text-slate-400">Individual experiences vary. Testimonials reflect individual patient experiences and do not guarantee that others will achieve the same results.</p>
      </div>
    </section>
  );
}

function CtaSection({navigate}) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-sky-700 to-blue-600 py-28 text-white">
      <div className="absolute -left-20 top-0 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
          <SafeIcon icon={FiLock} className="h-8 w-8" />
        </div>
        <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
          Ready to take the next step?
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-xl leading-relaxed text-sky-50">
          Complete a private online assessment and a licensed clinician will determine whether prescription treatment may be appropriate for you.
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <button onClick={()=>navigate('/menquiz')}
            className="group inline-flex items-center gap-3 rounded-full bg-white px-9 py-4 text-lg font-extrabold text-sky-800 shadow-xl transition hover:bg-sky-50 hover:shadow-2xl">
            Go to Men's Health Quiz
            <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
        <p className="mt-7 text-sm text-sky-100">
          Prescription medications require medical review. Eligibility and results vary by individual.
        </p>
      </div>
    </section>
  );
}

export default function MenRx() {
  const navigate = useNavigate();
  useEffect(()=>{window.scrollTo(0,0);},[]);
  return (
    <div className="overflow-hidden">
      <HeroSection navigate={navigate} />
      <TrustBar />
      <CareSection navigate={navigate} />
      <HowItWorksSection />
      <AuthoritySection navigate={navigate} />
      <TestimonialsSection />
      <CtaSection navigate={navigate} />
    </div>
  );
}